// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import './CollectReceived.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SubstituteRegistWasteUnit from '../SubstituteRegistWasteUnit';
import { SelectTenant } from '../SelectTenant';
import { Tenant } from 'components/molecules/SelectTenantCard';
import { Path } from 'routes';
import { PrintLabel, RoleType } from 'util/Enums';
import { Area, AreaGroup } from 'util/Types';
import { getCollectAreas, getCollectSiteGroups } from 'apis/collects';
import { useQuery, useUpdateQuery } from 'util/queryParams';
import { REGEX_OBJECT } from 'util/ConstantValues';
import { IConChevronLeft } from 'components/atoms/icons/IconChevronLeft';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { IconClose } from 'components/atoms/icons/IconClose';
import { mergeClasses } from 'util/commons';
import CollectAreasMap from 'components/molecules/CollectAreasMap';
import StackedRadiosGroup from 'components/atoms/StackedRadiosGroup';
import IconList from 'components/atoms/icons/IconList';
import IconMapView from 'components/atoms/icons/IconMapView';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import SearchedAreaGroup from './SearchedAreaGroup';

type Props = {
  area: Area;
};

const COLLECT_MAP_VIEW_MODE_STORAGE_KEY = 'COLLECT_MAP_VIEW_MODE';

export function CollectReceived() {
  const { t } = useTranslation();
  const queryParams = useQuery();
  const groupSiteId = queryParams.get('siteGroupId') || undefined;
  const keyword = queryParams.get('site-keyword') || undefined;
  const areaId = queryParams.get('areaId') || undefined;
  const tenantId = queryParams.get('tenantId') || undefined;
  const updateQueryParams = useUpdateQuery();
  const user = useSelector(selectUser);
  const router = useHistory();

  const [areas, setAreas] = useState<Area[]>([]);
  const [areaGroups, setAreaGroups] = useState<AreaGroup[]>([]);
  const [selectedArea, setSelectedArea] = useState<Area>();
  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const [isShowGroupInside, setIsShowGroupInside] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>(
    localStorage.getItem(COLLECT_MAP_VIEW_MODE_STORAGE_KEY) === 'list'
      ? 'list'
      : 'map'
  );
  const history = useHistory();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleUpdateViewMode = (mode: 'list' | 'map') => {
    setAreas([]);
    setAreaGroups([]);
    setViewMode(mode);
    localStorage.setItem(COLLECT_MAP_VIEW_MODE_STORAGE_KEY, mode);
  };

  const handleUpdateRouteGroup = (
    groupId: string | undefined,
    replace = false
  ) => {
    updateQueryParams({
      callback: (searchParams) => {
        if (groupId) {
          searchParams.set('siteGroupId', groupId);
        } else {
          searchParams.delete('siteGroupId');
        }
      },
      pathname: Path.collects,
      replace,
    });
  };

  const handleSearch = (keyword: string) => {
    updateQueryParams({
      callback: (searchParams) => {
        if (keyword) {
          searchParams.set('site-keyword', keyword);
        } else {
          searchParams.delete('site-keyword');
        }
      },
      pathname: Path.collects,
    });
  };

  const callApi = async () => {
    if (viewMode === 'map' && !groupSiteId) {
      await getCollectAreas({
        keyword,
        successCallback: (data) => {
          setAreas(data);
          setIsShowGroupInside(false);
        },
      });
    } else {
      await getCollectSiteGroups({
        groupSiteId,
        keyword,
        successCallback: (data) => {
          const areas: Area[] = [];
          const areaGroups: AreaGroup[] = [];

          data.forEach((item) => {
            if (item.type === 'collectingRoute') {
              areas.push(item);
            } else {
              areaGroups.push(item);
            }
          });

          setAreas(areas);
          setAreaGroups(groupSiteId != null ? [] : areaGroups);
          setIsShowGroupInside(groupSiteId != null);
        },
        failedCallback: () => {
          handleUpdateRouteGroup(undefined, true);
          setIsShowGroupInside(false);
        },
      });
    }
  };

  useEffect(() => {
    if (
      groupSiteId &&
      (!REGEX_OBJECT.number.test(groupSiteId) ||
        user.role !== RoleType.UserNormal)
    ) {
      handleUpdateRouteGroup(undefined, true);
      return;
    }

    callApi();
  }, [groupSiteId, keyword, user.role, viewMode]);

  useEffect(() => {
    if (!areaId || (!areas.length && !areaGroups.length)) {
      return;
    }

    const area = areas.find((a) => a.areaId === areaId);
    const areaInsideGroup = areaGroups
      .flatMap((group) => group.collectingRoutes || [])
      .find((a) => a.areaId === areaId);

    if (area || areaInsideGroup) {
      setSelectedArea(area || areaInsideGroup);
    } else {
      updateQueryParams({
        callback: (searchParams) => {
          searchParams.delete('areaId');
        },
        replace: true,
      });
    }
  }, [areaId, areas, areaGroups]);

  useEffect(() => {
    if (!tenantId || !selectedArea) {
      return;
    }

    const tenant = selectedArea.tenants?.find(
      (t) => t.id.toString() === tenantId
    );

    if (tenant) {
      setSelectedTenant(tenant);
    } else {
      updateQueryParams({
        callback: (searchParams) => {
          searchParams.delete('tenantId');
        },
        replace: true,
      });
    }
  }, [selectedArea, tenantId]);

  const renderCollectButton = (area: Area) => {
    // All buttons will use onClick to navigate to keep map state, please check CollectAreasMap for more details
    if (area.sitePrintLabel === PrintLabel.QRCode) {
      return (
        <a
          onClick={() => {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('siteId', area.siteId);
            history.push(
              `${Path.collectsQRScanning}?${searchParams.toString()}`
            );
          }}
          className="collect-area-button"
        >
          {t('collects.button_to_scan_qr_code')}
        </a>
      );
    }
    if (
      !area.substituteRegistRelations ||
      area.substituteRegistRelations.length === 0
    ) {
      return (
        <a
          className="collect-area-button"
          onClick={() => {
            history.push(
              `${Path.collects}/${area.areaId}${window.location.search}`
            );
          }}
        >
          {t('collects.button_to_recover')}
        </a>
      );
    }

    return (
      <a
        className="collect-area-button"
        onClick={() => {
          updateQueryParams({
            callback: (searchParams) => {
              searchParams.set('areaId', area.areaId);
            },
          });
        }}
      >
        {t('collects.button_to_substitute_regist')}
      </a>
    );
  };

  const sortAreas = (areas: Area[]) =>
    areas.sort((a, b) => {
      // First sort by emissionFlg (true first)
      if (a.emissionFlg !== b.emissionFlg) {
        return b.emissionFlg ? 1 : -1;
      }
      // Then sort by areaName within same emissionFlg
      return a.areaName.localeCompare(b.areaName);
    });

  // FIXME: We can sort on the server side
  const sortedAreas = useMemo(() => sortAreas(areas), [areas]);

  const sortedAreaGroups = useMemo(
    () => areaGroups.sort((a, b) => a.name.localeCompare(b.name)),
    [areaGroups]
  );

  if (selectedArea) {
    const wasteUnitIds =
      selectedArea.substituteRegistRelations.map(
        (relation) => relation.wasteUnitId
      ) || [];
    const uniqueWasteUnitIds = Array.from(new Set(wasteUnitIds));
    // only allow selecting tenant
    const tenants = selectedArea.tenants || [];
    if (tenants.length && !selectedTenant) {
      return (
        <SelectTenant
          sections={[]}
          oSections={[]}
          tenants={[]}
          oTenants={tenants}
          onSelectSection={() => {}}
          onSelectFloor={() => {}}
          onSelectTenant={(tenant) => {
            updateQueryParams({
              callback: (searchParams) => {
                searchParams.set('tenantId', tenant.id.toString());
              },
            });
          }}
          showActions={false}
          withPageReload={false}
          hasLogo={false}
          prev={() => {
            updateQueryParams({
              callback: (searchParams) => {
                searchParams.delete('areaId');
              },
            });
            setSelectedArea(undefined);
          }}
        />
      );
    }

    return (
      <SubstituteRegistWasteUnit
        wasteUnitIds={uniqueWasteUnitIds}
        siteId={selectedArea.siteId}
        tenantId={selectedTenant?.id}
        prev={() => {
          if (selectedTenant) {
            updateQueryParams({
              callback: (searchParams) => {
                searchParams.delete('tenantId');
              },
            });
            setSelectedTenant(undefined);
          } else {
            updateQueryParams({
              callback: (searchParams) => {
                searchParams.delete('areaId');
              },
            });
            setSelectedArea(undefined);
          }
        }}
      />
    );
  }

  const renderAreasList = (areas: Area[]) => (
    <div className="collect-area-list">
      {areas.map((area, i) => (
        <div key={i} className="collect-area-card">
          <div className="area-info">
            <CollectAreaStatusIcon area={area} />
            {area.areaName && <div className="area-name">{area.areaName}</div>}
            <div className="area-map">
              <p
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set('lng', area.areaLongitude.toString());
                  url.searchParams.set('lat', area.areaLatitude.toString());
                  url.searchParams.set('zoom', '13');
                  router.push(`${url.pathname}${url.search}`);

                  setTimeout(() => handleUpdateViewMode('map'), 0);
                }}
              >
                {t('collects.label_map')}
              </p>
            </div>
          </div>
          <div className="collect-button">{renderCollectButton(area)}</div>
        </div>
      ))}
    </div>
  );

  const renderAreaGroup = () => {
    if (!sortedAreaGroups.length || viewMode === 'map') {
      return null;
    }

    if (keyword) {
      return (
        <div className="site-group-search-list">
          {sortedAreaGroups.map((areaGroup) => (
            <SearchedAreaGroup
              key={areaGroup.id}
              areaGroup={areaGroup}
              handleUpdateRouteGroup={handleUpdateRouteGroup}
              sortAreas={sortAreas}
              renderAreasList={renderAreasList}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="site-group-list">
        {sortedAreaGroups.map(({ id, name }) => (
          <div
            key={id}
            className="site-group"
            onClick={() => handleUpdateRouteGroup(id)}
          >
            <p className="site-group-name">{name}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderAreas = () => {
    if (!sortedAreas.length) {
      return null;
    }

    if (viewMode === 'map') {
      return (
        <div className="areas-map-wrapper">
          <CollectAreasMap
            data={sortedAreas}
            renderCollectButton={renderCollectButton}
          />
        </div>
      );
    }

    return renderAreasList(sortedAreas);
  };

  const renderEmptyList = () => {
    if (areaGroups.length || areas.length) {
      return null;
    }

    return (
      <div className="empty-list">
        <p>{t('messages.M_008')}</p>
      </div>
    );
  };

  return (
    <div className="collectReceivedWrapper">
      <div className="collect-received scroll-contents">
        <div
          className={mergeClasses('action-bar', {
            'map-action-bar': viewMode === 'map',
          })}
        >
          {isShowGroupInside && (
            <button
              className="back-button"
              onClick={() => handleUpdateRouteGroup(undefined)}
            >
              <IConChevronLeft />
              <span>{t('common.return')}</span>
            </button>
          )}

          <StackedRadiosGroup
            classNames={{
              item: 'map-change-view-item',
              active: 'map-change-view-item-active',
            }}
            options={[
              { label: <IconMapView />, value: 'map' },
              { label: <IconList />, value: 'list' },
            ]}
            onChange={(val) => {
              handleUpdateViewMode(val as 'map' | 'list');
            }}
            value={viewMode}
          />

          <form
            className="search-input"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch(searchInputRef.current?.value.trim() || '');
              if (searchInputRef.current) {
                searchInputRef.current.value =
                  searchInputRef.current.value.trim();
              }
            }}
          >
            <div className="search-input-content">
              <input
                name="keyword"
                placeholder={t('common.button.search_site') || ''}
                ref={searchInputRef}
                defaultValue={keyword || ''}
                className={mergeClasses('search-input-field', {
                  'has-clear': !!keyword,
                })}
              />
              {keyword && (
                <span
                  className="search-clear"
                  onClick={() => {
                    handleSearch('');
                    if (searchInputRef.current) {
                      searchInputRef.current.value = '';
                    }
                  }}
                >
                  <IconClose strokeColor="#e7e7e7" width={12} height={12} />
                </span>
              )}
            </div>

            <button type="submit">
              <IconSearch color="black" />
            </button>
          </form>
        </div>

        {renderAreaGroup()}

        {renderAreas()}

        {renderEmptyList()}
      </div>
    </div>
  );
}

export function CollectAreaStatusIcon({
  area,
  className,
}: Props & {
  className?: string;
}) {
  const { t } = useTranslation();
  if (area.collectRequestFlg) {
    return (
      <div className={className}>
        <div className="icon status-collect-requested">
          {t('collects.label_collection_request')}
        </div>
      </div>
    );
  }

  if (area.emissionFlg) {
    return (
      <div className={className}>
        <div className="icon status-collect-requested status-collect-registered">
          {t('collects.label_collection_registered')}
        </div>
      </div>
    );
  }

  return null;
}
